import {
  initializeApp,
  getApps,
  getApp,
  setLogLevel,
  type FirebaseApp,
} from "firebase/app";
import {
  type Firestore,
  initializeFirestore,
  connectFirestoreEmulator,
  disableNetwork,
  enableNetwork,
} from "firebase/firestore";
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  type Auth,
  connectAuthEmulator,
} from "firebase/auth";
import {
  getRemoteConfig as firebaseGetRemoteConfig,
  type RemoteConfig,
} from "firebase/remote-config";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import queryString from "query-string";

import { firebaseRawConfig } from "./config";
import { enableAnalytics } from "utilities/analyticsConfig";
import { isAppleDevice, isAppleWebView } from "utilities/display";

const isSSR = typeof window === "undefined";
const isEmulatorMode = Boolean(
  firebaseRawConfig.projectId?.startsWith("demo-"),
);

let app: FirebaseApp;
let auth: Auth;
let firestore: Firestore;
let remoteConfig: RemoteConfig;

const REMOTE_CONFIG_FETCH_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

// Safari 26.4+ broke Firestore's fetch-stream WebChannel transport — WebKit only,
// Chrome is unaffected. On Apple (iOS Safari + the iOS WebView) we disable fetch
// streams to dodge the ~17s cold-load hang and force long polling to stop the
// mid-session realtime de-sync (useFetchStreams:false alone only half-helps).
// Every other platform keeps the SDK default. Both settings work around
// https://github.com/firebase/firebase-js-sdk/issues/9789; remove when it ships.
const firestoreTransportSettings = () =>
  isAppleDevice() || isAppleWebView()
    ? { experimentalForceLongPolling: true, useFetchStreams: false }
    : { experimentalAutoDetectLongPolling: true };

const getFirebaseApp = (): FirebaseApp =>
  getApps().length ? getApp() : initializeApp(firebaseRawConfig);

const initialiseAppCheck = (firebaseApp: FirebaseApp) => {
  const siteKey = process.env.GATSBY_APP_CHECK_SITE_KEY;
  if (isSSR || !siteKey) {
    return;
  }

  // "true" makes the SDK log a new debug token to register in the console
  const debugToken = process.env.GATSBY_APP_CHECK_DEBUG_TOKEN;
  if (debugToken) {
    self.FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugToken === "true" ? true : debugToken;
  }

  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
};

const createFirestore = (firebaseApp: FirebaseApp): Firestore => {
  const instance = initializeFirestore(
    firebaseApp,
    // the emulator runs locally and isn't affected by #9789, so it uses the
    // SDK default transport instead of the iOS long-polling workaround
    isEmulatorMode
      ? { experimentalAutoDetectLongPolling: true }
      : firestoreTransportSettings(),
    firebaseRawConfig.databaseId,
  );

  if (isEmulatorMode) {
    connectFirestoreEmulator(
      instance,
      firebaseRawConfig.emulator.firestoreHost,
      firebaseRawConfig.emulator.firestorePort,
    );
  }

  return instance;
};

const createAuth = (firebaseApp: FirebaseApp): Auth => {
  const instance = initializeAuth(firebaseApp, {
    persistence: [indexedDBLocalPersistence, browserLocalPersistence],
  });

  if (isEmulatorMode) {
    connectAuthEmulator(
      instance,
      `http://${firebaseRawConfig.emulator.authHost}:${firebaseRawConfig.emulator.authPort}`,
    );
  }

  return instance;
};

const configureDebugLogging = () => {
  const queryParams = isSSR ? {} : queryString.parse(window.location.search);
  if (
    queryParams.logFb !== undefined ||
    process.env.GATSBY_LOG_FIREBASE === "true"
  ) {
    setLogLevel("debug");
  }
};

const initialiseFirebase = () => {
  if (process.env.JEST_WORKER_ID !== undefined) {
    return; // skip during unit tests
  }

  app = getFirebaseApp();

  if (isEmulatorMode) {
    console.log(
      `Running in emulator mode with project: ${firebaseRawConfig.projectId}`,
    );
    auth = createAuth(app);
    firestore = createFirestore(app);
  } else {
    initialiseAppCheck(app);
    firestore = createFirestore(app);
    configureDebugLogging();
    auth = createAuth(app);

    if (!isSSR) {
      remoteConfig = firebaseGetRemoteConfig(app);
      remoteConfig.settings.minimumFetchIntervalMillis =
        REMOTE_CONFIG_FETCH_INTERVAL_MS;
    }
  }

  enableAnalytics();
};

const getFirestore = (): Firestore => {
  if (!firestore) {
    firestore = createFirestore(getFirebaseApp());
  }
  return firestore;
};

let reconnecting: Promise<void> | null = null;

const runReconnect = async () => {
  const instance = getFirestore();
  try {
    await disableNetwork(instance);
    await enableNetwork(instance);
  } finally {
    reconnecting = null;
  }
};

const reconnectFirestore = () => {
  if (!reconnecting) {
    reconnecting = runReconnect();
  }
  return reconnecting;
};

const getRemoteConfig = (): RemoteConfig | undefined => {
  if (isSSR) {
    return undefined;
  }
  if (isEmulatorMode) {
    console.log("Running in emulator mode, RemoteConfig not supported.");
    return undefined;
  }
  if (!remoteConfig) {
    remoteConfig = firebaseGetRemoteConfig(getFirebaseApp());
  }
  return remoteConfig;
};

initialiseFirebase();

export { auth, getFirestore, getRemoteConfig, reconnectFirestore };
